import { IHttpServiceConfig, IHttpMethodConfig, IHttpResponse } from './types';
import { validateRequestData, validateEndpoint } from './validation';
import { createAxiosInstance, getRecaptchaToken, handleError } from './utils';

let config: IHttpServiceConfig;
let axiosInstance: any;

export async function init(
	methods: Record<string, IHttpMethodConfig>,
	baseUrl: string,
	getToken: () => string,
	recaptchaSecret?: string
): Promise<void> {
	config = {
		methods,
		baseUrl,
		getToken,
		recaptchaSecret,
	};

	axiosInstance = createAxiosInstance(config);
}

export async function exec(
	name: string,
	data: Record<string, any> = {},
	additionalHeaders: Record<string, string> = {},
	additionalConfiguration: Record<string, string> = {}
): Promise<IHttpResponse> {
	if (!config) {
		throw new Error('HTTP Service not initialized. Call init() first.');
	}

	const method = config.methods[name];
	if (!method) {
		throw new Error(`Method ${name} not found in configuration.`);
	}

	// Validate request data
	const validationErrors = validateRequestData(method, data);
	if (validationErrors.length > 0) {
		throw new Error(`Validation errors: ${JSON.stringify(validationErrors)}`);
	}

	// Get recaptcha token if needed
	let recaptchaToken = '';
	if (!method.skipRecaptcha && config.recaptchaSecret) {
		recaptchaToken = await getRecaptchaToken(name, config.recaptchaSecret);
	}

	// Prepare headers
	const headers = {
		...method.headers,
		...additionalHeaders,
		...(recaptchaToken ? { 'x-one-digital-recaptcha-token': recaptchaToken } : {}),
	};

	// Prepare configuration
	const configuration = {
		...method.additionalConfigurations,
		...additionalConfiguration,
	};

	try {
		// Make the actual request
		const endpoint = validateEndpoint(method.endpoint, data);
		const response = await axiosInstance({
			method: method.httpMethod,
			url: endpoint,
			data: method.httpMethod === 'post' ? data : undefined,
			params: method.httpMethod === 'get' ? data : undefined,
			headers,
			...configuration,
		});

		return {
			data: response.data,
			status: response.status,
			statusText: response.statusText,
			headers: response.headers,
		};
	} catch (error) {
		return handleError(error);
	}
}

// Export types for use in other files
export type { IHttpMethodConfig, IHttpResponse, IHttpServiceConfig } from './types';
